// import {
//   ArrowPathIcon,
//   EnvelopeIcon,
//   HomeIcon,
// } from "@heroicons/react/24/outline";
import * as Sentry from "@sentry/react";
import { useErrorBoundary } from "@via/hooks/useErrorBoundary.tsx";
import React, { Component, useEffect } from "react";
import { /* Link,*/ useLocation } from "react-router-dom";
// import Logo from "/logo.svg";

interface ErrorBoundaryState {
  // hasError: boolean;
  // errorMessage: string | null;
  epoch: number;
  lastAt: number;
  count: number;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

// Class-based ErrorBoundary to catch React errors
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      // hasError: false,
      // errorMessage: null,
      epoch: 0,
      lastAt: 0,
      count: 0,
    };
  }

  // Update state when an error occurs
  // static getDerivedStateFromError(error: Error) {
  //   return { hasError: true, errorMessage: error.message };
  // }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
    // Report to Sentry
    try {
      Sentry.captureException(error, {
        extra: { componentStack: errorInfo.componentStack },
        tags: { boundary: "render" },
      });
    } catch (sentryError) {
      console.error("Sentry capture failed:", sentryError);
    }

    const now = Date.now();
    const within15s = now - (this.state.lastAt ?? 0) < 15000;
    const count = within15s ? (this.state.count ?? 0) + 1 : 1;

    // If too many crashes in a short window, stop remounting to prevent CPU spin.
    if (within15s && count > 10) {
      // Optional: show minimal inline note, or no-op to keep last good UI.
      console.warn("Too many re-renders after errors; suppressing remount.");
      return;
    }

    // Force a clean remount of children (no fallback UI)
    this.setState(s => ({ epoch: s.epoch + 1, lastAt: now, count }));
  }

  // resetError = () => {
  //   this.setState({ hasError: false, errorMessage: null });
  // };

  render() {
    const { /* hasError, errorMessage, */ epoch } = this.state;
    const { children } = this.props;

    // if (hasError) {
    //   console.error(errorMessage);
    //   return (
    //     <div className="flex h-screen flex-col items-center justify-center bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 p-6 text-center">
    //       {/* Logo */}
    //       <Link to="/" target="_blank">
    //         <img
    //           className="mx-auto h-20 w-auto transform transition-transform duration-500 ease-in-out hover:rotate-180"
    //           src={Logo}
    //           alt="Foundry Connect logo"
    //         />
    //       </Link>
    //       {/* Heading */}
    //       <h1 className="mt-8 text-4xl font-extrabold text-white drop-shadow-lg">
    //         Oops! Something went wrong.
    //       </h1>
    //       {/* Error Message */}
    //       <p className="mx-auto mt-4 max-w-lg text-lg text-white/90">
    //         {`An unexpected error occurred. We're sorry for the inconvenience.
    //           Please try again or contact support if the problem persists.`}
    //       </p>
    //       {/* Support Email */}
    //       <div className="mt-6 text-white/90">
    //         <EnvelopeIcon className="inline h-5 w-5 mr-2" />
    //         <a
    //           href="mailto:biocore@umassmed.edu"
    //           className="underline transition-colors duration-300 hover:text-white">
    //           biocore@umassmed.edu
    //         </a>
    //       </div>
    //       <div>
    //         {/* Try Again Button */}
    //         <button
    //           className="mt-10 mr-2 inline-flex items-center justify-center rounded-full bg-white px-6 py-3 font-bold text-indigo-600 shadow-lg transition-all duration-300 ease-in-out hover:bg-gray-100 hover:shadow-xl focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-300"
    //           onClick={this.resetError}>
    //           <ArrowPathIcon
    //             className="mr-2 h-5 w-5 stroke-2 text-indigo-600 duration-200 ease-in-out"
    //             aria-hidden="true"
    //           />
    //           Try Again
    //         </button>
    //         {/* Redirect Home Button */}
    //         <Link
    //           to="/"
    //           className="mt-2 inline-flex items-center justify-center rounded-full bg-white px-6 py-3 font-bold text-indigo-600 shadow-lg transition-all duration-300 ease-in-out hover:bg-gray-100 hover:shadow-xl focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-300">
    //           <HomeIcon
    //             className="mr-2 h-5 w-5 stroke-2 text-indigo-600 duration-200 ease-in-out"
    //             aria-hidden="true"
    //           />
    //           Go Home
    //         </Link>
    //       </div>
    //     </div>
    //   );
    // }

    return <React.Fragment key={epoch}>{children}</React.Fragment>;
  }
}

// Hook to handle global/unhandled errors outside of the component tree
const GlobalErrorBoundaryWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const {
    // displayErrorMessage,
    hasError,
    error,
    errorMessage,
    resetError,
  } = useErrorBoundary();
  const location = useLocation();

  useEffect(() => {
    // Reset error when the location (URL) changes
    if (hasError) resetError();
  }, [location, hasError, resetError]);

  useEffect(() => {
    if (!hasError) return;

    Sentry.captureException(
      error instanceof Error
        ? error
        : new Error(
            typeof errorMessage === "string" && errorMessage.trim().length > 0
              ? errorMessage
              : "Unhandled error"
          ),
      { tags: { boundary: "global" } }
    );
  }, [hasError, error, errorMessage]);

  // Temporarily disabling the global error boundary UI

  // if (hasError) {
  //   console.error(errorMessage);
  //   return (
  //     <div className="flex h-screen flex-col items-center justify-center bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 p-6 text-center">
  //       {/* Logo */}
  //       <Link to="/" target="_blank">
  //         <img
  //           className="mx-auto h-20 w-auto transform transition-transform duration-500 ease-in-out hover:rotate-180"
  //           src={Logo}
  //           alt="Foundry Connect logo"
  //         />
  //       </Link>
  //       {/* Heading */}
  //       <h1 className="mt-8 text-4xl font-extrabold text-white drop-shadow-lg">
  //         Oops! Something went wrong.
  //       </h1>
  //       {/* Error Message */}
  //       <p className="mx-auto mt-4 max-w-lg text-lg text-white/90">
  //         {displayErrorMessage
  //           ? errorMessage
  //           : `An unexpected error occurred. We're sorry for the inconvenience.
  //           Please try again or contact support if the problem persists.`}
  //       </p>
  //       {/* Support Email */}
  //       <div className="mt-6 text-white/90">
  //         <EnvelopeIcon className="inline h-5 w-5 mr-2" />
  //         <a
  //           href="mailto:biocore@umassmed.edu"
  //           className="underline transition-colors duration-300 hover:text-white">
  //           biocore@umassmed.edu
  //         </a>
  //       </div>
  //       <div>
  //         {/* Try Again Button */}
  //         <button
  //           className="mt-10 mr-2 inline-flex items-center justify-center rounded-full bg-white px-6 py-3 font-bold text-indigo-600 shadow-lg transition-all duration-300 ease-in-out hover:bg-gray-100 hover:shadow-xl focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-300"
  //           onClick={resetError}>
  //           <ArrowPathIcon
  //             className="mr-2 h-5 w-5 stroke-2 text-indigo-600 duration-200 ease-in-out"
  //             aria-hidden="true"
  //           />
  //           Try Again
  //         </button>
  //         {/* Redirect Home Button */}
  //         <Link
  //           to="/"
  //           className="inline-flex items-center justify-center rounded-full bg-white px-6 py-3 font-bold text-indigo-600 shadow-lg transition-all duration-300 ease-in-out hover:bg-gray-100 hover:shadow-xl focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-300">
  //           <HomeIcon
  //             className="mr-2 h-5 w-5 stroke-2 text-indigo-600 duration-200 ease-in-out"
  //             aria-hidden="true"
  //           />
  //           Go Home
  //         </Link>
  //       </div>
  //     </div>
  //   );
  // }

  return <ErrorBoundary>{children}</ErrorBoundary>;
};

export default GlobalErrorBoundaryWrapper;
